/**
 * Read queries over the local database shared by the clients, so UI components contain no SQL
 * (docs/07 §4.2, CONTRIBUTING). Each returns the statement and its parameters for the SDK's
 * reactive queries (`useQuery(sql, params)`).
 */
export const queries = {
    /** A query that returns no rows, for hooks that must run before their inputs exist. */
    none() {
        return { sql: 'SELECT NULL AS none WHERE 0', params: [] };
    },
    /** The user's Personal workspace (exactly one per user, docs/03 §4). */
    personalWorkspace() {
        return {
            sql: "SELECT id, name, kind FROM workspaces WHERE kind = 'personal' AND deleted_at IS NULL LIMIT 1",
            params: [],
        };
    },
    /** Preferences of the signed-in user (one row per user, id = user_id). */
    userPreferences(userId) {
        return {
            sql: 'SELECT id, theme, density, locale, timezone FROM user_preferences WHERE user_id = ? LIMIT 1',
            params: [userId],
        };
    },
    /**
     * System views of the user, in the order the template installed them (docs/05 §3). Watched by
     * the sidebar: read through `by_user` (user, sort key) and capped, like every watched query.
     */
    sidebarViews(userId, limit = 50) {
        return {
            sql: `SELECT id, key, name, icon, sidebar_section, sort_key, layout
            FROM views
            WHERE user_id = ? AND deleted_at IS NULL
            ORDER BY sort_key
            LIMIT ?`,
            params: [userId, limit],
        };
    },
    /** Rituals of the user, for the «Rituales» section of the sidebar. */
    sidebarRituals(userId, limit = 20) {
        return {
            sql: 'SELECT id, key, name, schedule FROM rituals WHERE user_id = ? ORDER BY name LIMIT ?',
            params: [userId, limit],
        };
    },
    /** A view of the user by its template key, to resolve the route `/v/$viewKey`. */
    viewByKey(userId, key) {
        return {
            sql: `SELECT id, key, name, icon, sidebar_section, sort_key, layout
            FROM views
            WHERE user_id = ? AND key = ? AND deleted_at IS NULL
            LIMIT 1`,
            params: [userId, key],
        };
    },
    /** Stages of a workspace (the sidebar counts by stage; the views arrive in their own changes). */
    stages(workspaceId, limit = 50) {
        return {
            sql: `SELECT id, key, name, category FROM stages
            WHERE workspace_id = ? AND archived_at IS NULL
            ORDER BY sort_key LIMIT ?`,
            params: [workspaceId, limit],
        };
    },
    /**
     * Open blocks of a stage, counted with a cap (design D2): the inner select is bounded by the
     * `by_stage` index and by `LIMIT`, so a save never waits for a count over the whole workspace
     * (f1-local-write-latency). Above the cap the sidebar shows «99+».
     */
    openBlocksInStage(stageId, cap = 100) {
        return {
            sql: `SELECT count(*) AS n FROM (
              SELECT 1 FROM blocks
              WHERE stage_id = ? AND status = 'open' AND deleted_at IS NULL
              LIMIT ?
            )`,
            params: [stageId, cap],
        };
    },
    /**
     * Overdue follow-ups (SEG-02): open blocks of the waiting stage whose follow-up date is today
     * or earlier. Driven by the `by_field` index of `field_values` and capped like the counters.
     */
    overdueFollowUps(stageId, fieldId, today, cap = 100) {
        return {
            sql: `SELECT count(*) AS n FROM (
              SELECT 1 FROM field_values fv
              JOIN blocks b ON b.id = fv.block_id
              WHERE fv.field_id = ? AND fv.value_date IS NOT NULL AND fv.value_date <= ?
                AND b.stage_id = ? AND b.status = 'open' AND b.deleted_at IS NULL
              LIMIT ?
            )`,
            params: [fieldId, today, stageId, cap],
        };
    },
    /** Custom fields of the workspace: the list views read the ones they group or sort by. */
    fieldDefs(workspaceId, limit = 100) {
        return {
            sql: `SELECT id, key, type FROM field_defs
            WHERE workspace_id = ? AND archived_at IS NULL
            LIMIT ?`,
            params: [workspaceId, limit],
        };
    },
    /** A custom field of the workspace by its key (the sidebar needs `follow_up_on` for SEG-02). */
    fieldDefByKey(workspaceId, key) {
        return {
            sql: `SELECT id, key, type FROM field_defs
            WHERE workspace_id = ? AND key = ? AND archived_at IS NULL
            LIMIT 1`,
            params: [workspaceId, key],
        };
    },
    /** Stage where captured items land (`is_capture_default`, docs/03 §3.1). */
    captureStage(workspaceId) {
        return {
            sql: 'SELECT id, key, name FROM stages WHERE workspace_id = ? AND is_capture_default = 1 AND archived_at IS NULL LIMIT 1',
            params: [workspaceId],
        };
    },
    /**
     * Destinations of the quick capture (CAP-03): the task stages of the workspace, capture stage
     * included (INV-04). Few rows, through the `by_workspace` index of `stages`.
     */
    captureDestinations(workspaceId, limit = 20) {
        return {
            sql: `SELECT id, workspace_id, key, name, category, kind_scope, is_capture_default FROM stages
            WHERE workspace_id = ? AND kind_scope = 'task' AND archived_at IS NULL
            ORDER BY sort_key LIMIT ?`,
            params: [workspaceId, limit],
        };
    },
    /**
     * Every stage of the workspace, of tasks and of projects, for the detail panel: «Mover a…»
     * offers the ones of the block's kind and the type conversion picks its destination
     * (f1-type-conversion D1, D5).
     */
    workspaceStages(workspaceId, limit = 50) {
        return {
            sql: `SELECT id, workspace_id, key, name, category, kind_scope, is_capture_default FROM stages
            WHERE workspace_id = ? AND archived_at IS NULL
            ORDER BY sort_key LIMIT ?`,
            params: [workspaceId, limit],
        };
    },
    /** Taxonomies with a capture prefix (`@ # +`) of the workspace (docs/06 §2). */
    captureTaxonomies(workspaceId, limit = 20) {
        return {
            sql: `SELECT id, workspace_id, key, prefix, cardinality FROM taxonomies
            WHERE workspace_id = ? AND prefix IS NOT NULL
            LIMIT ?`,
            params: [workspaceId, limit],
        };
    },
    /**
     * Tags of the prefixed taxonomies, to resolve the parsed ones against (design D3). Read once
     * when the capture opens, not watched. Archived tags count (the server's name uniqueness
     * includes them) and merged ones resolve to their target; deleted ones do not exist.
     * Driven by the `by_taxonomy` index and capped.
     */
    captureTags(workspaceId, limit = 5000) {
        return {
            sql: `SELECT t.id, t.taxonomy_id, t.parent_id, t.name, t.merged_into_id
            FROM taxonomies x JOIN tags t ON t.taxonomy_id = x.id
            WHERE x.workspace_id = ? AND x.prefix IS NOT NULL AND t.deleted_at IS NULL
            LIMIT ?`,
            params: [workspaceId, limit],
        };
    },
    /**
     * Open elements of the Inbox, oldest first (docs/04 §2 rule 1), with their tags: the Inbox view
     * and the triage queue watch it. Read in order through `by_stage (stage, status, created_at)`
     * and capped; the tags come from `block_tags.by_block` for each returned row only.
     */
    inboxItems(stageId, limit = 200) {
        return {
            sql: `SELECT b.id, b.workspace_id, b.title, b.kind, b.stage_id, b.stage_changed_at, b.status,
                   b.completed_at, b.deleted_at, b.due_on, b.start_on, b.created_at,
                   (SELECT json_group_array(json_object('id', t.id, 'name', t.name, 'prefix', x.prefix))
                      FROM block_tags bt
                      JOIN tags t ON t.id = bt.tag_id
                      JOIN taxonomies x ON x.id = t.taxonomy_id
                     WHERE bt.block_id = b.id) AS tags
              FROM blocks b
             WHERE b.stage_id = ? AND b.status = 'open' AND b.deleted_at IS NULL
             ORDER BY b.created_at
             LIMIT ?`,
            params: [stageId, limit],
        };
    },
    /** The user's open period (INV-09: at most one), read when deciding a TAI. */
    openPeriod(userId) {
        return {
            sql: `SELECT id, started_at FROM periods
            WHERE user_id = ? AND closed_at IS NULL
            ORDER BY started_at DESC LIMIT 1`,
            params: [userId],
        };
    },
    /** A highlight type of the user by key (`tai` in GTD+PARA), with its limit and eligibility. */
    highlightTypeByKey(userId, key) {
        return {
            sql: `SELECT id, key, label, max_per_period, limit_mode, eligible_kind, eligible_stage_category, pin_top
            FROM highlight_types WHERE user_id = ? AND key = ? LIMIT 1`,
            params: [userId, key],
        };
    },
    /**
     * Highlights of a type in a period that are not removed, achieved ones included (TAI-03), with
     * the block title for the limit dialog. Through `highlights.by_period`.
     */
    activeHighlights(userId, periodId, typeId, limit = 50) {
        return {
            sql: `SELECT h.id, h.block_id, h.rank, h.achieved_at, b.title
            FROM highlights h LEFT JOIN blocks b ON b.id = h.block_id
            WHERE h.user_id = ? AND h.period_id = ? AND h.highlight_type_id = ?
              AND h.removed_at IS NULL
            ORDER BY h.rank LIMIT ?`,
            params: [userId, periodId, typeId, limit],
        };
    },
    /** Custom field values of a block (the follow-up data the triage may overwrite and restore). */
    blockFieldValues(blockId, limit = 50) {
        return {
            sql: `SELECT id, workspace_id, block_id, field_id, value_text, value_number, value_date,
                   value_ts, value_bool, value_ref, value_json, created_at
            FROM field_values WHERE block_id = ? LIMIT ?`,
            params: [blockId, limit],
        };
    },
    /**
     * One block by its id, for the detail panel (f1-detail-panel): its columns plus the title of its
     * parent for the breadcrumbs. Read by primary key; archived and trashed blocks are left out, so
     * the panel closes when the block goes to the trash.
     */
    blockDetail(blockId) {
        return {
            sql: `SELECT b.id, b.workspace_id, b.parent_id, b.title, b.body, b.kind, b.status,
                   b.stage_id, b.stage_changed_at, b.due_on, b.start_on, b.sort_key,
                   b.completed_at, b.created_at, b.props,
                   (SELECT p.title FROM blocks p WHERE p.id = b.parent_id LIMIT 1) AS parent_title
            FROM blocks b
            WHERE b.id = ? AND b.deleted_at IS NULL AND b.archived_at IS NULL
            LIMIT 1`,
            params: [blockId],
        };
    },
    /**
     * Current title, kind and status of the blocks a search found (f1-trash-search, design D6): the
     * search is a one-shot read, this keeps the shown results in step with edits and deletions. By
     * primary key, with `LIMIT`.
     */
    blocksByIds(ids) {
        if (ids.length === 0) {
            return {
                sql: 'SELECT NULL AS id, NULL AS title, NULL AS kind, NULL AS status WHERE 0',
                params: [],
            };
        }
        return {
            sql: `SELECT id, title, kind, status FROM blocks
            WHERE id IN (${ids.map(() => '?').join(', ')}) AND deleted_at IS NULL
            LIMIT ?`,
            params: [...ids, ids.length],
        };
    },
    /**
     * Trashed blocks of a workspace, for the Trash (f1-trash-search, design D2). Bounded by the
     * `by_trash` index and by `LIMIT`; roots, descendants and order are worked out in JS.
     */
    trashedBlocks(workspaceId, limit = 500) {
        return {
            sql: `SELECT id, parent_id, title, kind, status, deleted_at FROM blocks
            WHERE workspace_id = ? AND deleted_at IS NOT NULL
            LIMIT ?`,
            params: [workspaceId, limit],
        };
    },
    /**
     * Live descendants of a block, parents before children, read when trashing it (not watched).
     * The depth is capped so a malformed tree can never loop.
     */
    liveDescendants(blockId, limit = 2000) {
        return {
            sql: `WITH RECURSIVE d(id, depth) AS (
              SELECT id, 1 FROM blocks WHERE parent_id = ? AND deleted_at IS NULL
              UNION ALL
              SELECT b.id, d.depth + 1 FROM blocks b JOIN d ON b.parent_id = d.id
              WHERE b.deleted_at IS NULL AND d.depth < 32
            )
            SELECT id FROM d ORDER BY depth LIMIT ?`,
            params: [blockId, limit],
        };
    },
    /** Descendants trashed with a block (its same mark), parents first, read when restoring it. */
    trashedWith(blockId, deletedAt, limit = 2000) {
        return {
            sql: `WITH RECURSIVE d(id, deleted_at, depth) AS (
              SELECT id, deleted_at, 1 FROM blocks WHERE parent_id = ? AND deleted_at = ?
              UNION ALL
              SELECT b.id, b.deleted_at, d.depth + 1 FROM blocks b JOIN d ON b.parent_id = d.id
              WHERE b.deleted_at = ? AND d.depth < 32
            )
            SELECT id, deleted_at FROM d ORDER BY depth LIMIT ?`,
            params: [blockId, deletedAt, deletedAt, limit],
        };
    },
    /**
     * Trashed ancestors of a block, from its parent up to the first live one (INV-12): restoring
     * the block brings them back so it is not left under a trashed parent.
     */
    trashedAncestors(blockId) {
        return {
            sql: `WITH RECURSIVE a(id, parent_id, deleted_at, depth) AS (
              SELECT p.id, p.parent_id, p.deleted_at, 1 FROM blocks c JOIN blocks p ON p.id = c.parent_id
              WHERE c.id = ? AND p.deleted_at IS NOT NULL
              UNION ALL
              SELECT p.id, p.parent_id, p.deleted_at, a.depth + 1 FROM blocks p JOIN a ON p.id = a.parent_id
              WHERE p.deleted_at IS NOT NULL AND a.depth < 32
            )
            SELECT id, deleted_at FROM a ORDER BY depth LIMIT 32`,
            params: [blockId],
        };
    },
    /**
     * Subtasks of a block with their title, for the detail panel. Through `by_parent`, without
     * ordering in SQL: sorting by `sort_key` there would need a temporary sort of what it found
     * (the rule of watched queries), and the panel orders the handful of rows in JS.
     */
    subtasksOf(parentId, limit = 200) {
        return {
            sql: `SELECT id, parent_id, title, status, kind, sort_key, completed_at FROM blocks
            WHERE parent_id = ? AND deleted_at IS NULL
            LIMIT ?`,
            params: [parentId, limit],
        };
    },
    /** Tags of a block with their taxonomy, for the detail panel. Through `block_tags.by_block_taxonomy`. */
    blockTagsOf(blockId, limit = 100) {
        return {
            sql: `SELECT bt.tag_id, bt.taxonomy_id, t.name, t.sort_key, x.key AS taxonomy_key,
                   x.prefix, x.cardinality
            FROM block_tags bt
            JOIN tags t ON t.id = bt.tag_id
            JOIN taxonomies x ON x.id = bt.taxonomy_id
            WHERE bt.block_id = ? AND t.deleted_at IS NULL
            LIMIT ?`,
            params: [blockId, limit],
        };
    },
    /**
     * Tags offered by the pickers of the detail panel: every taxonomy of the workspace at once, not
     * merged, not archived, not deleted, in the order of each taxonomy.
     */
    workspaceTags(workspaceId, limit = 2000) {
        return {
            sql: `SELECT id, taxonomy_id, name, sort_key, parent_id FROM tags
            WHERE workspace_id = ? AND deleted_at IS NULL AND archived_at IS NULL
              AND merged_into_id IS NULL
            ORDER BY sort_key
            LIMIT ?`,
            params: [workspaceId, limit],
        };
    },
    /** Taxonomies of the workspace, for the detail panel (area, context, topic, person). */
    workspaceTaxonomies(workspaceId, limit = 20) {
        return {
            sql: `SELECT id, workspace_id, key, name, prefix, cardinality, sort_key FROM taxonomies
            WHERE workspace_id = ?
            ORDER BY sort_key
            LIMIT ?`,
            params: [workspaceId, limit],
        };
    },
    /**
     * Children of the listed blocks (subtask progress «n/m», EJE-09), not deleted: rows, not counts,
     * so no aggregate runs over `blocks` in a watched query (design D2). Through `by_parent`.
     */
    childrenOf(parentIds, limit = 2000) {
        if (parentIds.length === 0) {
            return {
                sql: 'SELECT NULL AS id, NULL AS parent_id, NULL AS status, NULL AS kind, NULL AS completed_at WHERE 0',
                params: [],
            };
        }
        return {
            sql: `SELECT id, parent_id, status, kind, completed_at FROM blocks
            WHERE parent_id IN (${parentIds.map(() => '?').join(', ')}) AND deleted_at IS NULL
            LIMIT ?`,
            params: [...parentIds, limit],
        };
    },
    /** A view of the user by key with its options, for the list views (VIS-01). */
    viewSettings(userId, key) {
        return {
            sql: `SELECT id, key, name, filter, group_by, sort, options, layout
            FROM views
            WHERE user_id = ? AND key = ? AND deleted_at IS NULL
            LIMIT 1`,
            params: [userId, key],
        };
    },
    /** Actionable blocks of a workspace (INV-13): open first, then done; siblings by sort key. */
    actionableBlocks(workspaceId, limit = 200) {
        // Test list of F0: the newest `limit` open blocks (then done ones), shown in list order. Without
        // a limit a workspace with 50.000 blocks renders tens of thousands of rows (seconds on iPhone).
        // Watched query: each group is read newest first through `by_workspace_sort` and stops at the
        // limit; ordering by `(status = 'done')` in one query would read and sort the whole workspace
        // after every local save (f1-local-write-latency). Actionable blocks always have a status (INV-03).
        const group = (condition) => `SELECT * FROM (
         SELECT id, title, kind, status, sort_key, completed_at FROM blocks
         WHERE workspace_id = ? AND deleted_at IS NULL AND archived_at IS NULL
           AND kind IN ('item', 'task', 'project') AND ${condition}
         ORDER BY sort_key DESC
         LIMIT ?
       )`;
        return {
            sql: `SELECT id, title, kind, status, sort_key, completed_at FROM (
              SELECT * FROM (
                ${group("(status = 'done') IS NOT 1")}
                UNION ALL
                ${group("status = 'done'")}
              )
              ORDER BY (status = 'done') ASC, sort_key DESC
              LIMIT ?
            )
            ORDER BY (status = 'done') ASC, sort_key ASC`,
            params: [workspaceId, limit, workspaceId, limit, limit],
        };
    },
    /** Last sort key among the actionable blocks of a workspace (to append at the end). */
    lastSortKey(workspaceId) {
        // Watched query: read the key through `by_workspace_sort` from the end instead of `max()`
        // over every block of the workspace (f1-local-write-latency). Always one row.
        return {
            sql: `SELECT (
              SELECT sort_key FROM blocks
              WHERE workspace_id = ? AND deleted_at IS NULL AND kind IN ('item', 'task', 'project')
              ORDER BY sort_key DESC
              LIMIT 1
            ) AS last`,
            params: [workspaceId],
        };
    },
    taxonomyByKey(workspaceId, key) {
        return {
            sql: 'SELECT id, key, name FROM taxonomies WHERE workspace_id = ? AND key = ? LIMIT 1',
            params: [workspaceId, key],
        };
    },
    tagByName(taxonomyId, name) {
        return {
            sql: 'SELECT id, name FROM tags WHERE taxonomy_id = ? AND name = ? AND deleted_at IS NULL LIMIT 1',
            params: [taxonomyId, name],
        };
    },
    /** One block by id (read that follows a local save in the perf page). */
    blockById(id) {
        return { sql: 'SELECT id, title, status FROM blocks WHERE id = ?', params: [id] };
    },
    /** Live blocks of a workspace (perf page). */
    countBlocks(workspaceId) {
        return {
            sql: 'SELECT count(*) AS n FROM blocks WHERE workspace_id = ? AND deleted_at IS NULL',
            params: [workspaceId],
        };
    },
    /** A project that has children (perf page: subtree recalculation). */
    projectWithChildren(workspaceId) {
        return {
            sql: `SELECT p.id FROM blocks p
            WHERE p.workspace_id = ? AND p.kind = 'project' AND p.deleted_at IS NULL
              AND EXISTS (SELECT 1 FROM blocks c WHERE c.parent_id = p.id)
            LIMIT 1`,
            params: [workspaceId],
        };
    },
    /** Whether the local database already holds data received from the server (1) or not (0). */
    hasServerData() {
        return { sql: 'SELECT EXISTS (SELECT 1 FROM ps_oplog) AS had', params: [] };
    },
    /** Number of local operations waiting to be uploaded (SYN-03 indicator). */
    /**
     * Local changes not yet uploaded, counted by transaction: one capture writes its block, tags and
     * joins together and is one change for the user (f1-quick-capture). A row without a transaction
     * counts on its own.
     */
    pendingUploads() {
        return { sql: 'SELECT count(DISTINCT COALESCE(tx_id, -id)) AS n FROM ps_crud', params: [] };
    },
    /** Rejections of the user not yet acknowledged (SYN-05). */
    unacknowledgedRejections() {
        return {
            sql: 'SELECT id, table_name, row_id, op, reason_code, message, created_at FROM sync_rejections WHERE acknowledged_at IS NULL ORDER BY created_at DESC LIMIT 20',
            params: [],
        };
    },
};
//# sourceMappingURL=queries.js.map